// Pure constants for the Nutrition Progress engine.
// No React / Rill coupling — see ./index.js for the public barrel.

// The four plan-status states the persistent status bar can render.
export const PLAN_STATUS = {
  ACTIVE_MEAL: "active_meal", // active plan today + meal plan assigned
  ACTIVE: "active", // active plan today, no meal plan
  DUE: "due", // most recent plan already ended → due for renewal
  NONE: "none", // no plan to measure the period against
};

// Quick-select ranges for the range controls. Each is a trailing window
// ending "today". `amount` + `unit` feed moment's subtract().
// "lifetime" is a sentinel — the hook resolves it to the earliest plan date.
export const QUICK_RANGES = [
  { id: "1W",       amount: 1,  unit: "weeks"  },
  { id: "2W",       amount: 2,  unit: "weeks"  },
  { id: "1M",       amount: 1,  unit: "months" },
  { id: "3M",       amount: 3,  unit: "months" },
  { id: "6M",       amount: 6,  unit: "months" },
  { id: "lifetime", amount: 10, unit: "years"  },
];

// Default quick-select used when there is no active plan to anchor the range.
export const DEFAULT_QUICK_RANGE = "2W";

// Quick-select ranges for the standalone Bodyweight trend chart
// (BodyweightTrendCard.js, Summary view) — separate set from QUICK_RANGES
// above: coaches narrow the main nutrition period to 1-2 weeks, but a
// bodyweight trend needs a much longer window to read past day-to-day
// noise (water/glycogen), per product lead feedback. Always resolves
// against real "today", independent of whatever nutrition period is
// selected elsewhere — same {amount, unit} + "lifetime" sentinel shape as
// QUICK_RANGES, resolved by the same resolveQuickRange() (dateRange.js).
export const BODYWEIGHT_QUICK_RANGES = [
  { id: "1M",       amount: 1,  unit: "months" },
  { id: "3M",       amount: 3,  unit: "months" },
  { id: "6M",       amount: 6,  unit: "months" },
  { id: "1Y",       amount: 1,  unit: "years"  },
  { id: "lifetime", amount: 10, unit: "years"  },
];

export const DEFAULT_BODYWEIGHT_QUICK_RANGE = "3M";

// Rules for the Bodyweight trend card's calculations (bodyweightTrend.js).
// All weights are kg — only the display layer converts to lb.
export const TREND_WINDOW_DAYS = 7; // 7-day average: this many calendar days ending that day
export const RATE_WINDOW_DAYS = 28; // rate of change compares the trend now with the trend this many days earlier
export const ON_PACE_TOLERANCE_KG = 0.2; // projected weight within ± this of the goal → on pace
export const GOAL_DIRECTION_THRESHOLD_KG = 0.2; // goal this far above/below the trend → gain/lose, else maintain
// A weigh-in this far from the trend (as a fraction of the reference trend)
// is flagged off-trend. A fraction, not a fixed kg, so it scales across
// clients. Pending product/design confirmation: the design's example spike
// (+0.5 kg on ~60.3 kg) is ~0.83%, below this threshold.
export const OFF_TREND_PCT = 0.01;
export const OFF_TREND_MIN_PRIOR = 3; // weigh-ins needed before one can be flagged off-trend
export const STALE_WEIGHIN_DAYS = 3; // the "Last weigh-in" tile turns amber after more days than this
// A last weigh-in older than this is too old to say where the client is
// heading: no rate, projection or pace verdict (the projection would run from
// that old date out to the goal date). Pending product confirmation.
export const PROJECTION_MAX_STALE_DAYS = 28;
// Wording of the no-goal rate tile: below STEADY the client is "holding
// steady", up to SLOW_MAX it is moving "slowly", above that "quickly". Both in
// kg/week. Defaults pending product confirmation.
export const RATE_STEADY_KG_PER_WEEK = 0.1;
export const RATE_SLOW_MAX_KG_PER_WEEK = 0.5;

// Adherence color thresholds. Configurable per plan/client eventually;
// these are the defaults. Functions in adherence.js return semantic tokens
// (good/warn/bad/none) using these — the view maps tokens → colors.
export const ADHERENCE_THRESHOLDS = {
  bandOnTarget: 10, // day deviation < 10 → on target
  bandSlightlyOff: 25, // < 25 → slightly off, else off
  macroGood: 0.05, // |Δ|/target < 5% → good (per-macro cell, stricter)
  macroWarn: 0.15, // < 15% → warn, else bad
  adhGood: 90, // kcal-adherence % ≥ 90 → good
  adhWarn: 75, // ≥ 75 → warn, else bad
};
